import { useState } from "react";
import { toast } from "sonner";
import { Briefcase, MapPin, Send, GraduationCap } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CTA from "@/components/sections/CTA";
import { api } from "@/lib/api";
import { CAREERS_OPEN } from "@/lib/content";

export default function Careers({ onOpenPalette }: { onOpenPalette: () => void }) {
  const [form, setForm] = useState({ name: "", email: "", role: "", portfolio_url: "", cover_letter: "", cv_url: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) { toast.error("Please fill the required fields."); return; }
    setLoading(true);
    try {
      await api.post("/careers", form);
      toast.success("Application received ✨ — we'll be in touch!");
      setDone(true);
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Navbar onOpenPalette={onOpenPalette} />
      <main className="pt-28 pb-12 page-fade" data-testid="careers-page">
        <div className="container-edx pt-16 max-w-5xl">
          <div className="section-index">EDX.01</div>
          <h1 className="display mt-4 text-4xl md:text-6xl text-white">Build the <span className="gradient-text">future</span> with us.</h1>
          <p className="mt-4 text-edx-muted max-w-2xl">
            EDENOX is hiring across software, AI, design and operations. We run an annual graduate programme too.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {CAREERS_OPEN.map((j, i) => (
              <div key={j.role} data-testid={`job-${i}`} className="glass glass-edge p-6 tilt">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-edx-purple/15 text-edx-purple flex items-center justify-center">
                    {j.level === "Graduate" ? <GraduationCap size={18} /> : <Briefcase size={18} />}
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-edx-cyan border border-edx-cyan/30 rounded-full px-2 py-0.5">{j.level}</span>
                </div>
                <h3 className="display font-bold text-lg text-white mt-4">{j.role}</h3>
                <div className="text-xs text-edx-muted flex items-center gap-1 mt-1"><MapPin size={11} /> {j.type}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {j.tags.map(t => <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-edx-muted">{t}</span>)}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <h2 className="display text-2xl md:text-3xl text-white">Apply now</h2>
            <p className="text-edx-muted text-sm mt-1">Or send us a speculative application — we always want to meet ambitious people.</p>

            {done ? (
              <div className="mt-8 glass p-12 text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-edx-cyan to-edx-purple mx-auto flex items-center justify-center">
                  <Send size={24} className="text-white" />
                </div>
                <div className="display text-2xl text-white mt-5">Application received.</div>
                <p className="text-edx-muted mt-2">We review every application personally. Expect to hear from us within 2 weeks.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="careers-form" className="mt-6 glass p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name *"><input data-testid="careers-name" className="edx-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
                <Field label="Email *"><input data-testid="careers-email" type="email" className="edx-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></Field>
                <Field label="Role *" className="sm:col-span-2"><select data-testid="careers-role" className="edx-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} required>
                  <option value="">Choose a role…</option>
                  {CAREERS_OPEN.map(j => <option key={j.role} value={j.role} className="bg-edx-eclipse">{j.role}</option>)}
                  <option value="Speculative" className="bg-edx-eclipse">Speculative</option>
                </select></Field>
                <Field label="Portfolio / LinkedIn URL" className="sm:col-span-2"><input data-testid="careers-portfolio" className="edx-input" value={form.portfolio_url} onChange={e => setForm({ ...form, portfolio_url: e.target.value })} placeholder="https://…" /></Field>
                <Field label="Cover letter" className="sm:col-span-2"><textarea data-testid="careers-cover" className="edx-input min-h-[140px]" value={form.cover_letter} onChange={e => setForm({ ...form, cover_letter: e.target.value })} placeholder="Tell us why you want to join EDENOX…" /></Field>
                <Field label="CV link (Google Drive, Dropbox, etc.)" className="sm:col-span-2"><input data-testid="careers-cv" className="edx-input" value={form.cv_url} onChange={e => setForm({ ...form, cv_url: e.target.value })} placeholder="https://…" /></Field>
                <div className="sm:col-span-2">
                  <button type="submit" data-testid="careers-submit" disabled={loading} className="btn btn-primary w-full sm:w-auto disabled:opacity-60">
                    {loading ? "Submitting…" : "Submit application"} <Send size={14} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
        <CTA />
      </main>
      <Footer />
    </>
  );
}

function Field({ label, children, className = "" }: any) {
  return <div className={className}><label className="text-[11px] uppercase tracking-widest text-edx-muted block mb-2 font-mono">{label}</label>{children}</div>;
}
